import type {
  AddComponentBody,
  ApproveBody,
  ComponentConfig,
  ComponentsResponse,
  ConfigHistoryEntry,
  ConfigSaveBody,
  ConflictBody,
  IncidentsResponse,
  ManualProposalBody,
  Proposal,
  ProposalDetail,
  PublishFailedBody,
  PublishResult,
  RedecideBody,
  RejectBody,
  RetryBody,
  RevertBody,
  RunHistoryPage,
  Scope,
  ScopeConfig,
  UnprocessableBody,
} from '@/domain/types';

/**
 * The console API (TDD §11.2), as one interface. `HttpConsoleApi` speaks to the API lambda;
 * `MockConsoleApi` is a stateful in-memory stand-in used until the lambda exists and in tests.
 * Screens only ever see this interface.
 */
export interface ConsoleApi {
  getComponents(scope: Scope): Promise<ComponentsResponse>;
  getRuns(scope: Scope, capabilityId: string, opts?: RunsQuery): Promise<RunHistoryPage>;
  getQueue(scope: Scope): Promise<Proposal[]>;
  getProposal(proposalId: string): Promise<ProposalDetail>;
  getIncidents(scope: Scope): Promise<IncidentsResponse>;

  approve(proposalId: string, body: ApproveBody): Promise<PublishResult>;
  reject(proposalId: string, body: RejectBody): Promise<{ proposal: Proposal }>;
  redecide(proposalId: string, body: RedecideBody): Promise<PublishResult>;
  retry(proposalId: string, body: RetryBody): Promise<PublishResult>;
  createManualProposal(body: ManualProposalBody): Promise<PublishResult>;

  getConfig(scope: Scope, capabilityId: string): Promise<ComponentConfig | ScopeConfig>;
  saveConfig(
    scope: Scope,
    capabilityId: string,
    body: ConfigSaveBody,
  ): Promise<{ config: ComponentConfig | ScopeConfig }>;
  addComponent(scope: Scope, body: AddComponentBody): Promise<{ config: ComponentConfig }>;
  getConfigHistory(scope: Scope, capabilityId: string): Promise<ConfigHistoryEntry[]>;
  revertConfig(
    scope: Scope,
    capabilityId: string,
    body: RevertBody,
  ): Promise<{ config: ComponentConfig | ScopeConfig }>;
}

export interface RunsQuery {
  limit?: number;
  before?: string;
}

/**
 * Every non-2xx response becomes an ApiError. The three bodies the TDD specifies (409, 422,
 * 502) are exposed through typed accessors so screens can show the current state, the
 * violations, or the failed proposal without re-parsing.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? describe(status, body));
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  get conflict(): ConflictBody | null {
    return this.status === 409 && isRecord(this.body) && 'current' in this.body
      ? (this.body as unknown as ConflictBody)
      : null;
  }

  get unprocessable(): UnprocessableBody | null {
    return this.status === 422 && isRecord(this.body) && 'error' in this.body
      ? (this.body as unknown as UnprocessableBody)
      : null;
  }

  get publishFailed(): PublishFailedBody | null {
    return this.status === 502 && isRecord(this.body) && 'proposal' in this.body
      ? (this.body as unknown as PublishFailedBody)
      : null;
  }

  /** Field-level messages from a 422, keyed by field, for inline form errors. */
  get violations(): Record<string, string> {
    const list = this.unprocessable?.violations ?? [];
    return Object.fromEntries(list.map((v) => [v.field, v.message]));
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function describe(status: number, body: unknown): string {
  if (isRecord(body)) {
    if (typeof body.detail === 'string') return body.detail;
    if (typeof body.last_error === 'string') return `Publish failed: ${body.last_error}`;
    if (typeof body.error === 'string') return body.error.replaceAll('_', ' ');
  }
  switch (status) {
    case 409:
      return 'Someone or something changed this since you loaded it.';
    case 422:
      return 'The action is not allowed in the current state.';
    case 502:
      return 'The publish failed after three tries.';
    default:
      return `The console API answered ${status}.`;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}
